import React, { useEffect, useRef, useState } from 'react';
import leagueName from '../utils/leagueName';
import Modal from 'react-bootstrap/Modal';
import './AdminResultCorrections.css';
import AdminMatchScore from './AdminMatchScore';

const displayDate = (day) => new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
});
const outcome = (winners) => winners.length ? winners.join(', ') : 'No winning selections (all bets lose)';

export default function AdminResultCorrections({ user, scores }) {
  const [markets, setMarkets] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState(null);
  const [winners, setWinners] = useState([]);
  const [stage, setStage] = useState('edit');
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!user) return undefined;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`/api/admin/settled-markets?page=${page}&search=${encodeURIComponent(search)}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to load settled markets.');
        if (!controller.signal.aborted) {
          setMarkets(data.markets);
          setHasMore(data.hasMore);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setMarkets([]);
          setHasMore(false);
          setNotice({ type: 'danger', text: err.message });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [user, page, search, revision]);

  const request = async (path, body) => {
    const token = await user.getIdToken();
    const response = await fetch(path, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.error || 'Unable to correct this result.');
    return data;
  };

  const open = (market) => {
    setSelected(market);
    setWinners(market.winners);
    setPreview(null);
    setStage('edit');
    setConfirmation('');
    setError('');
  };
  const close = () => { if (!inFlight.current) setSelected(null); };

  const review = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await request('/api/admin/result-corrections/preview', { ...selected, winningBets: winners });
      setPreview(result);
      setStage('review');
    } catch (err) { setError(err.message); }
    finally { inFlight.current = false; setBusy(false); }
  };

  const correct = async () => {
    if (inFlight.current || stage !== 'confirm' || confirmation !== 'REVERT') return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await request('/api/admin/result-corrections', {
        ...preview.market, winningBets: preview.winners,
        expectedVersion: preview.expectedVersion, confirmation,
      });
      setSelected(null);
      setNotice({ type: 'success', text: `Corrected ${result.betCount} bets. Wallets were adjusted and affected players notified.` });
      setRevision((value) => value + 1);
    } catch (err) {
      setError(err.message);
      // A timeout may mean the server committed. Always obtain a fresh preview.
      setStage('edit');
      setPreview(null);
      setConfirmation('');
      setRevision((value) => value + 1);
    } finally { inFlight.current = false; setBusy(false); }
  };

  return (
    <section className='admin-corrections col-lg-10 col-sm-12 m-auto text-white text-start'>
      <div className='bg-green shadow-down rounded p-3 mb-5 match-list admin-tab-panel'>
        <h2 className='h5'>Settled results</h2>
        <p class = "small text-lightgrey">Revert an incorrect outcome and apply the correct result together. Both confirmations are required.</p>
        <label htmlFor='settled-market-search' className='visually-hidden'>Search match</label>
        <div className='col-8 searchgroup text-end d-flex align-items-center mb-3'>
          <span className='searchgroup-text' aria-hidden='true'>
            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'
              fill='currentColor' className='bi bi-search' viewBox='0 0 16 16'>
              <path d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0' />
            </svg>
          </span>
          <input id='settled-market-search' type='text' className='search' value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); setNotice(null); }} placeholder='Search team...' />
        </div>
        {notice && <div role='status' className={`alert alert-${notice.type}`}>{notice.text}</div>}
        {loading ? <div role='status' className='py-3'>Loading settled results…</div> : (
          <>
            {!markets.length && <p>No settled results found.</p>}
            {markets.map((market) => (
              <article className='admin-correction-market p-3 mb-2 rounded' key={`${market.match_day}|${market.time}|${market.fixture}|${market.bet_market}`}>
                <h3 className='h6 mb-1'>{market.fixture}</h3>
                <div className='small text-lightgrey'>{leagueName(market.sport_keys)}</div>
                <div>{displayDate(market.match_day)} · {market.time} UTC · {market.bet_market}</div>
                <AdminMatchScore fixture={market.fixture} matchDay={market.match_day} time={market.time} scores={scores} hideOutsideLookupWindow hideDisclaimer />
                <div className='my-2'>Current winners: <strong>{outcome(market.winners)}</strong></div>
                <button type='button' className='btn btn-prim text-white' onClick={() => open(market)}>Revert &amp; correct</button>
              </article>
            ))}
            <nav aria-label='Settled results pages' className='d-flex align-items-center justify-content-between gap-2 mt-3'>
              <button type='button' className='btn btn-outline-light' disabled={page === 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
              <span>Page {page}</span>
              <button type='button' className='btn btn-outline-light' disabled={!hasMore} onClick={() => setPage((value) => value + 1)}>Next</button>
            </nav>
          </>
        )}
      </div>

      <Modal show={Boolean(selected)} onHide={close} centered scrollable size='lg'
        backdrop={busy ? 'static' : true} keyboard={!busy} className='admin-correction-modal'
        backdropClassName='admin-correction-backdrop' aria-labelledby='correction-title'>
        <Modal.Header closeButton={!busy} className='bg-green text-white'>
          <Modal.Title id='correction-title' as='h2' className='h5'>
            {stage === 'edit' ? 'Revert & correct result' : stage === 'review' ? 'Confirmation 1 of 2 — review changes' : 'Confirmation 2 of 2 — final confirmation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='bg-green text-white'>
          {selected && <>
            <p><strong>{selected.fixture}</strong><br />{displayDate(selected.match_day)} · {selected.time} UTC · {selected.bet_market}</p>
            {error && <div role='alert' className='alert alert-danger'>{error}</div>}
            {stage === 'edit' ? (
              <fieldset disabled={busy}>
                <legend className='h6'>Select the correct winning outcome</legend>
                {selected.options.map((option) => (
                  <label key={option} className='d-flex align-items-center gap-2 mb-2'>
                    <input type={selected.bet_market === '1x2' ? 'radio' : 'checkbox'} name='corrected-outcome'
                      checked={winners.includes(option)} onChange={() => setWinners((current) => selected.bet_market === '1x2'
                        ? [option] : current.includes(option) ? current.filter((item) => item !== option) : [...current, option])} />
                    {option}
                  </label>
                ))}
                {selected.bet_market === '1x2' && <label className='d-flex align-items-center gap-2 mb-2'>
                  <input type='radio' name='corrected-outcome' checked={winners.length === 0} onChange={() => setWinners([])} />
                  No winning selections (all bets lose)
                </label>}
                <p className='mt-3 mb-0'>Original payouts will be reversed, then the corrected payouts applied. Stakes are not deducted again.</p>
              </fieldset>
            ) : <>
              <p>From: <strong>{outcome(preview.previousWinners)}</strong><br />To: <strong className='text-orange'>{outcome(preview.winners)}</strong></p>
              <p>{preview.betCount} bets across {preview.userCount} players. Each player receives a correction notice and an updated result for each bet.</p>
              <div className='admin-correction-wallets'>
                <table className='table text-white'>
                  <caption className='text-white'>Wallet preview — balances are checked again when you confirm.</caption>
                  <thead><tr><th>Player</th><th>Before</th><th>Change</th><th>After</th></tr></thead>
                  <tbody>{preview.wallets.map((wallet) => <tr key={wallet.username}>
                    <td>{wallet.username}</td><td>{wallet.before}</td><td>{wallet.adjustment}</td><td>{wallet.after}</td>
                  </tr>)}</tbody>
                </table>
              </div>
              {stage === 'confirm' && <>
                <p className='mt-3'>This will immediately adjust wallets, replace bet results, and send notifications.</p>
                <label htmlFor='correction-confirmation'>Type <strong>REVERT</strong> to confirm</label>
                <input key='final-confirmation' autoFocus id='correction-confirmation' className='form-control' autoComplete='off'
                  value={confirmation} disabled={busy} onChange={(event) => setConfirmation(event.target.value)} />
              </>}
            </>}
          </>}
        </Modal.Body>
        <Modal.Footer className='bg-green border-0'>
          <button type='button' className='btn btn-outline-light' disabled={busy} onClick={close}>Cancel</button>
          {stage !== 'edit' && <button type='button' className='btn btn-outline-light' disabled={busy}
            onClick={() => { setStage('edit'); setPreview(null); setConfirmation(''); }}>Change outcome</button>}
          {stage === 'edit' && <button type='button' className='btn btn-prim text-white' disabled={busy || !user} onClick={review}>{busy ? 'Checking…' : 'Review correction'}</button>}
          {stage === 'review' && <button type='button' className='btn btn-prim text-white' onClick={() => { setStage('confirm'); setConfirmation(''); }}>Confirm outcome</button>}
          {stage === 'confirm' && <button type='button' className='btn btn-danger' disabled={busy || confirmation !== 'REVERT'} onClick={correct}>{busy ? 'Correcting…' : 'Confirm revert & correct'}</button>}
        </Modal.Footer>
      </Modal>
    </section>
  );
}
